import "../ScrollBarStyle.css";

import podcastIcon from "@assets/images/Podcast.svg";
import videoIcon from "@assets/images/YouTube.svg";
import MediaThumbnail from "@components/Layouts/HomePageStoryPackages/MediaPackages/MediaThumbnail";
import MoreLink from "@components/Layouts/HomePageStoryPackages/MoreLink";
import { IS_PLAYWRIGHT_TEST } from "@helper/getEnvVariables";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { MoreLinkProps } from "@typings/StoryPackage.d";
import { FC, HTMLAttributes } from "react";
import SimpleBar from "simplebar-react";
import { twMerge } from "tailwind-merge";

import { Variant } from "./types/MediaVariant.d";

interface ThumbnailItem {
  id: string;
  published_at?: string;
  name?: string;
  thumbnail?: string;
  src?: string;
  poster?: string;
}
export type MediaCardProps<T> = {
  variant: "Podcasts" | Variant;
  renderedThumbnail?: (T & ThumbnailItem)[];
  handleOnClickCallback: (source: string) => void;
  moreLink?: MoreLinkProps;
};

export default function MediaCard<T>({
  variant,
  renderedThumbnail,
  handleOnClickCallback,
  moreLink,
}: MediaCardProps<T>) {
  const thumbnailItems = Array.isArray(renderedThumbnail)
    ? renderedThumbnail
    : [];

  const sliderWidth = "w-[2150px]";

  const ScrollWrapper =
    IS_PLAYWRIGHT_TEST === "true"
      ? ({ children, className, style }: HTMLAttributes<HTMLDivElement>) => (
          <div
            className={className}
            style={{ overflowX: "auto", height: "100%", ...style }}
          >
            {children}
          </div>
        )
      : (SimpleBar as FC<HTMLAttributes<HTMLDivElement>>);

  const THUMBNAIL_HEIGHT = {
    Videos: 155,
    Ringkas: 366,
    Podcasts: 206,
  } as const;

  const thumbnailHeight = THUMBNAIL_HEIGHT[variant];

  return (
    <div className="my-sm py-xs border border-brand-primary border-x-0 border-b-0 border-t-2">
      <div className="flex items-center gap-xs font-primary text-sm font-bold text-brand-primary leading-[24px] mb-xs pl-xxs">
        <img
          src={variant === "Podcasts" ? podcastIcon : videoIcon}
          alt="media-icon"
          width={20}
          height={20}
        />
        <Heading className="font-primary">{variant}</Heading>
      </div>

      <ScrollWrapper
        style={{ height: "100%", overflowX: "auto" }}
        className={twMerge("scrollBarStyles pb-5")}
      >
        <div className={twMerge("flex gap-2.5", sliderWidth)}>
          {thumbnailItems.map((item, index) => (
            <button
              className="flex flex-col gap-sm text-left basis-[17%] break-words overflow-auto"
              key={index}
              onClick={() => {
                handleOnClickCallback(
                  variant === "Podcasts" ? (item.src ?? "") : item.id,
                );
              }}
            >
              <MediaThumbnail
                src={
                  variant === "Podcasts"
                    ? item.thumbnail
                    : (item.poster ?? item.thumbnail)
                }
                height={thumbnailHeight}
              />
              <div className="flex flex-col gap-sm">
                {item.name && (
                  <Heading className="lg:text-sm font-semibold font-secondary text-text-body leading-[19.2px] hover:text-brand-primary">
                    {item.name}
                  </Heading>
                )}
                {item.published_at && (
                  <span className="text-xs text-text-muted">
                    {item.published_at}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </ScrollWrapper>

      <div className="flex justify-end leading-[18px] text-neutral-700 font-bold font-primary border-border-strong border-t-[1px] pt-xs">
        {moreLink && (
          <MoreLink
            {...moreLink}
            className={twMerge("flex gap-1", moreLink.className)}
          />
        )}
      </div>
    </div>
  );
}
